// ─── Sprint 093 — Operator Center™ Menu ──────────────────────────────────────
// Opens when the operator taps their avatar. Lists all top-level features.

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { useLogout, getGetMeQueryKey } from '@workspace/api-client-react';
import {
  User, Target, BarChart2, Trophy, BookOpen,
  Backpack, Vault, CreditCard, Settings,
  Palette, Bell, Star, LogOut, ChevronRight,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { loadPersona, getInitials, clearLocalPersona } from '@/services/OperatorPersonaService';
import { getPendingCount } from '@/services/RoamerBackpackService';
import type { OperatorPersona } from '@/types/operatorPersona';

interface OperatorCenterMenuProps {
  open: boolean;
  onClose: () => void;
}

type MenuItem =
  | { kind: 'nav';      label: string; icon: React.ElementType; href: string;   badge?: number }
  | { kind: 'action';   label: string; icon: React.ElementType; action: string; destructive?: boolean }
  | { kind: 'divider' };

export function OperatorCenterMenu({ open, onClose }: OperatorCenterMenuProps) {
  const [persona, setPersona] = useState<OperatorPersona | null>(null);
  const [badge, setBadge]     = useState(0);
  const [, setLocation]       = useLocation();
  const queryClient           = useQueryClient();
  const logout                = useLogout();

  useEffect(() => {
    if (open) {
      setPersona(loadPersona());
      setBadge(getPendingCount());
    }
  }, [open]);

  function navigate(href: string) {
    onClose();
    setLocation(href);
  }

  function handleLogout() {
    onClose();

    // Shared cleanup — runs whether server-side deletion succeeds or fails.
    const wipeClientState = () => {
      queryClient.clear();
      // clearLocalPersona() wipes localStorage only — see Shell.tsx for the full
      // explanation of why clearPersona() (with cloud DELETE) must not be used here.
      clearLocalPersona();
      const USER_SCOPED_KEYS = [
        'nomad_activation_completed_v1',
        'nomad:roamer-profile:v1',
        'nomad:session-continuity:v1',
        'nomad:first-boot-running',
        'nomad:cloud-sync-meta:v1',
      ];
      for (const key of USER_SCOPED_KEYS) {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
      try { sessionStorage.removeItem('wr-intro-seen'); } catch { /* ignore */ }
      try { sessionStorage.removeItem('wr-welcome-seen'); } catch { /* ignore */ }
      try { sessionStorage.removeItem('nomad:wizard-active'); } catch { /* ignore */ }
      try { sessionStorage.removeItem('nomad:first-boot-launched'); } catch { /* ignore */ }
    };

    logout.mutate(undefined, {
      onSuccess: () => { wipeClientState(); setLocation('/login'); },
      onError:   () => { wipeClientState(); setLocation('/login'); },
    });
  }

  const initials    = persona?.displayName ? getInitials(persona.displayName) : 'OP';
  const displayName = persona?.displayName ?? 'Operator';

  const ITEMS: MenuItem[] = [
    { kind: 'nav',    label: 'Profile',           icon: User,       href: '/settings'       },
    { kind: 'nav',    label: 'Goals',              icon: Target,     href: '/settings'       },
    { kind: 'nav',    label: 'Statistics',         icon: BarChart2,  href: '/dashboard'      },
    { kind: 'nav',    label: 'Achievements',       icon: Trophy,     href: '/dashboard'      },
    { kind: 'divider' },
    { kind: 'nav',    label: 'Roamer Vault™',     icon: Vault,      href: '/learn-to-roam', badge },
    { kind: 'divider' },
    { kind: 'nav',    label: 'Payment Command™',  icon: CreditCard, href: '/payment-center' },
    { kind: 'divider' },
    { kind: 'nav',    label: 'Settings',           icon: Settings,   href: '/settings'       },
    { kind: 'action', label: 'Appearance',         icon: Palette,    action: 'appearance'    },
    { kind: 'action', label: 'Notifications',      icon: Bell,       action: 'notifications' },
    { kind: 'action', label: 'Subscription',       icon: Star,       action: 'subscription'  },
    { kind: 'divider' },
    { kind: 'action', label: 'Sign Out',           icon: LogOut,     action: 'logout', destructive: true },
  ];

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent
        style={{
          background:   '#0d1117',
          border:       '1px solid rgba(139,92,246,0.25)',
          borderRadius: 16,
          padding:      0,
          maxWidth:     340,
          overflow:     'hidden',
        }}
      >
        {/* Profile header */}
        <div style={{
          background:  'linear-gradient(135deg, rgba(30,58,138,0.6) 0%, rgba(76,29,149,0.6) 100%)',
          borderBottom:'1px solid rgba(255,255,255,0.06)',
          padding:     '20px 20px 16px',
          display:     'flex',
          alignItems:  'center',
          gap:         12,
        }}>
          {/* Avatar */}
          <div style={{
            width:          52,
            height:         52,
            borderRadius:   '50%',
            overflow:       'hidden',
            border:         '2px solid rgba(139,92,246,0.5)',
            background:     persona?.avatarDataUrl ? 'transparent' : 'linear-gradient(135deg,#1e3a8a,#4c1d95)',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            flexShrink:     0,
          }}>
            {persona?.avatarDataUrl
              ? <img src={persona.avatarDataUrl} alt={displayName} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
              : <span style={{ fontFamily:'monospace', fontSize:16, fontWeight:700, color:'#e2e8f0' }}>{initials}</span>
            }
          </div>
          <div>
            <div style={{ color:'#f1f5f9', fontWeight:700, fontSize:15, fontFamily:'monospace' }}>
              {displayName}
            </div>
            <div style={{
              display:    'flex',
              alignItems: 'center',
              gap:        4,
              marginTop:  2,
            }}>
              <span style={{
                width:8, height:8, borderRadius:'50%', background:'#22c55e', flexShrink:0,
              }} />
              <span style={{ color:'#22c55e', fontSize:10, fontFamily:'monospace', letterSpacing:'0.1em' }}>
                ONLINE
              </span>
            </div>
          </div>
        </div>

        {/* Menu items */}
        <div style={{ padding: '8px 0', maxHeight: '60vh', overflowY: 'auto' }}>
          {ITEMS.map((item, idx) => {
            if (item.kind === 'divider') {
              return <div key={idx} style={{ height:1, background:'rgba(255,255,255,0.05)', margin:'4px 0' }} />;
            }

            const Icon = item.icon;
            const isDestructive = item.kind === 'action' && item.destructive;

            return (
              <button
                key={idx}
                onClick={() => {
                  if (item.kind === 'nav') {
                    navigate(item.href);
                  } else if (item.action === 'logout') {
                    handleLogout();
                  }
                  // Appearance, Notifications, Subscription — navigate to settings
                  else {
                    navigate('/settings');
                  }
                }}
                style={{
                  display:        'flex',
                  alignItems:     'center',
                  gap:            10,
                  width:          '100%',
                  padding:        '10px 16px',
                  background:     'transparent',
                  border:         'none',
                  cursor:         'pointer',
                  color:          isDestructive ? '#f87171' : '#cbd5e1',
                  fontSize:       13,
                  fontFamily:     'monospace',
                  letterSpacing:  '0.03em',
                  textAlign:      'left',
                  transition:     'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = isDestructive ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.04)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <Icon style={{ width:15, height:15, flexShrink:0, opacity:0.75 }} />
                <span style={{ flex:1 }}>{item.label}</span>
                {item.kind === 'nav' && (item as {badge?: number}).badge ? (
                  <span style={{
                    background:    'linear-gradient(135deg,#8b5cf6,#3b82f6)',
                    color:         '#fff',
                    fontSize:      9,
                    fontWeight:    700,
                    borderRadius:  8,
                    padding:       '1px 6px',
                    fontFamily:    'monospace',
                  }}>
                    {(item as {badge?: number}).badge}
                  </span>
                ) : (
                  <ChevronRight style={{ width:12, height:12, opacity:0.3, flexShrink:0 }} />
                )}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
